import { Box, Button, TextField, Typography } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useCreateLabel } from '../../../../hooks/requests/use-labels';
import { runMutation } from '../../../../utils/vy-mutation';
import { LabelColorPicker } from './LabelColorPicker';
import { DEFAULT_LABEL_COLOR } from './labels-consts';
import type { LabelColorPreset } from './labels-types';

type CreateLabelFormProps = {
  onCancel: () => void;
  onSuccess?: (labelId: string) => void;
};

export const CreateLabelForm: FC<CreateLabelFormProps> = ({ onCancel, onSuccess }) => {
  const [name, setName] = useState('');
  const [selectedColor, setSelectedColor] = useState<LabelColorPreset>(DEFAULT_LABEL_COLOR);

  const createLabelMutation = useCreateLabel();

  const handleSubmit = useCallback(() => {
    if (!name.trim()) {
      return;
    }

    void runMutation(
      createLabelMutation,
      {
        name: name.trim(),
        color: selectedColor.color,
        backgroundColor: selectedColor.backgroundColor,
      },
      {
        pending: 'Creating label...',
        success: 'Label created',
        error: 'Failed to create label',
      },
      {
        onSuccess: (data) => {
          setName('');
          setSelectedColor(DEFAULT_LABEL_COLOR);
          onSuccess?.(data.label.id);
        },
      },
    );
  }, [name, selectedColor, createLabelMutation, onSuccess]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && name.trim()) {
        handleSubmit();
      } else if (event.key === 'Escape') {
        onCancel();
      }
    },
    [handleSubmit, onCancel, name],
  );

  const isSubmitDisabled = !name.trim() || createLabelMutation.isLoading;

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          mb: 1,
          display: 'block',
          fontWeight: 300,
          fontSize: '12px',
          lineHeight: '16px',
        }}
      >
        Choose a color
      </Typography>
      <LabelColorPicker selectedColor={selectedColor} onColorSelect={setSelectedColor} />

      <TextField
        data-vy="labels-create-name-input"
        value={name}
        onChange={(changeEvent) => setName(changeEvent.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Label name"
        size="small"
        fullWidth
        autoFocus
        sx={{
          mt: 2,
          '& .MuiInputBase-root': {
            height: '40px',
            borderRadius: '8px',
            bgcolor: 'common.white',
            border: '1px solid',
            borderColor: 'divider',
            fontSize: '14px',
            fontWeight: '300',
            pl: 0,
          },
        }}
      />

      <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
        <Button
          data-vy="labels-create-cancel"
          variant="text"
          size="small"
          sx={{
            borderRadius: '4px',
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
            height: '30px',
            lineHeight: '16px',
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-vy="labels-create-submit"
          variant="contained"
          size="small"
          sx={{ flex: 1, height: '30px', lineHeight: '16px', fontWeight: 300 }}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          + Add
        </Button>
      </Box>
    </Box>
  );
};
